import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { buildQueryString, showError, showSuccess, trimData } from "../../../utils/utilities";
import { useFormik } from "formik";
import { List, Row, Col, Modal, Spin, Input, Select } from "antd";
import { get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { langDate, paginationDefaults } from "../../../utils/properties";
import Filter from "../../../components/functional_components/Filter/Filter";
import {
  EditOutlined,
  WarningOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { setFilters } from "../../../redux/ducks/Filters";
import { useNavigate } from "react-router-dom";
import ProjectEstimate from "../../../services/api/ProjectEstimate";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";
import Currency from "../../../services/api/Currency";
import Vertical from "../../../services/api/Vertical";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import dayjs from "dayjs";
const { confirm } = Modal;

const NotAssociatedEstimate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const [state, setState] = useState({
    statusListFilter: [],
    tableContent: [],
    expandedTableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
      currentPageProjectEstimate: paginationDefaults.page,
      totalCurrentProjectEstimate: paginationDefaults.totalItems,
    },
    loading: false,
    filterLoading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    verticalsList: [],
    currenciesList: [],
    verticalListFilter: [],
  });

  const formikFilter = useFormik({
    initialValues: {
      year: filters.obj.year ? dayjs(filters.obj.year, "YYYY") : null,
      projectEstimateStatusId: filters.obj.projectEstimateStatusId ? filters.obj.projectEstimateStatusId : null,
      description: filters.obj.description ? filters.obj.description : "",
      verticalId: filters.obj.verticalId ? filters.obj.verticalId : "",
      preventiveId: filters.obj.preventiveId ? filters.obj.preventiveId : null,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      values = trimData(values);
      const queryParams = {
        year: values.year !== null ? dayjs(values.year, "YYYY").format("YYYY") : "",
        projectEstimateStatusId: values.projectEstimateStatusId,
        description: values.description,
        preventiveId: values.preventiveId,
      };

      if (values.verticalId) {
        queryParams.verticalId = values.verticalId;
      }

      const queryString = buildQueryString(filters.obj, queryParams);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  useEffect(() => {
    getStatusList();
    getVerticalsList();
    const queryParams = {
      year: "",
      associated: "false",
      projectEstimateStatusId: "",
      description: "",
    };
    const queryString = buildQueryString(filters.obj, queryParams);
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const getStatusList = async () => {
    const statuses = await ProjectEstimateStatus.getStatusesList("allPages=true");
    const optionList = [];
    if (!statuses.errors && statuses.data) {
      statuses.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({ ...prevState, statusListFilter: optionList }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
  };

  const deleteCallback = async (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const response = await ProjectEstimate.projectEstimateDelete(id);
        if (!response.error) {
          showSuccess();
          dataList(filters.string);
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const deleteIfPossible = (item) => {
    if (userInfo.canEditCostEstimation) {
      if (item.deletable) {
        return () => deleteCallback(item.id);
      } else {
        return () => showError(t("errorsCode.ER010"));
      }
    } else {
      return () => showError(t("errorsCode.ER003"));
    }
  };

  const dataList = async (querystring) => {
    dispatch(setLoading(true));
    const notProjectEstimate = await ProjectEstimate.projectEstimateList(querystring);
    if (!notProjectEstimate.error && notProjectEstimate.data) {
      notProjectEstimate.data.forEach((client, i) => {
        client.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(notProjectEstimate, "data", []),
        pagination: {
          currentPage: notProjectEstimate.currentPage ? notProjectEstimate.currentPage + 1 : paginationDefaults.page,
          size: get(notProjectEstimate, "pageSize", paginationDefaults.size),
          totalItems: get(notProjectEstimate, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(notProjectEstimate, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const fieldsFilter = [
    {
      type: "select",
      isRequired: false,
      id: "verticalId",
      name: "verticalId",
      value: formikFilter.values.verticalId,
      label: t("projectsManagement.filter.vertical"),
      onChange: formikFilter.handleChange("verticalId"),
      onBlur: formikFilter.handleBlur("verticalId"),
      dataSelect: [{ label: t("projectsManagement.filter.select.all"), value: "" }, ...state.verticalListFilter],
    },
    {
      type: "select",
      isRequired: false,
      id: "projectEstimateStatusId",
      name: "projectEstimateStatusId",
      value: formikFilter.values.projectEstimateStatusId,
      label: t("projectEstimate.filter.status"),
      onChange: formikFilter.handleChange("projectEstimateStatusId"),
      onBlur: formikFilter.handleBlur("projectEstimateStatusId"),
      dataSelect: state.statusListFilter,
    },
    {
      type: "year",
      id: "year",
      name: "year",
      format: "YYYY",
      allowClear: true,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectEstimate.filter.year"),
      value: formikFilter.values.year,
      onChange: (date) => {
        formikFilter.setFieldValue("year", date);
      },
    },
    {
      type: "input",
      id: "preventiveId",
      name: "preventiveId",
      value: formikFilter.values.preventiveId,
      label: t("projectEstimate.detail.tabs.preventiveForm.preventiveId"),
      onChange: formikFilter.handleChange("preventiveId"),
      onBlur: formikFilter.handleBlur("preventiveId"),
      disabled: formikFilter.values.description ? true : false,
    },
    {
      type: "input",
      isRequired: false,
      id: "description",
      name: "description",
      value: formikFilter.values.description,
      label: t("projectEstimate.filter.description"),
      onChange: formikFilter.handleChange("description"),
      onBlur: formikFilter.handleBlur("description"),
      disabled: formikFilter.values.preventiveId ? true : false,
    },
  ];

  const resetFilter = () => {
    delete filters.obj.verticalId;
    delete filters.obj.preventiveId;
    const queryParams = {
      projectEstimateStatusId: "",
      year: "",
      description: "",
    };
    const queryString = buildQueryString(filters.obj, queryParams);
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const formikProjectEstimate = useFormik({
    initialValues: {
      verticalId: get(state.modal.data, "vertical", ""),
      projectEstimateStatusId: get(state.modal.data, "status", ""),
      description: get(state.modal.data, "description", ""),
      currencyId: get(state.modal.data, "currency", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.verticalId) {
        errors.vertical = "Required!";
      }
      if (!values.projectEstimateStatusId) {
        errors.status = "Required!";
      }
      if (!values.description) {
        errors.description = "Required!";
      }
      if (!values.currencyId) {
        errors.currency = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const response = await ProjectEstimate.saveNewProjectEstimate(values);
      if (!response.error && response.data) {
        setState((prevState) => ({ ...prevState, loading: true }));
        const queryParams = {
          associated: "false",
          projectEstimateStatusId: "",
        };
        const queryString = buildQueryString(filters.obj, queryParams);
        dispatch(setFilters(queryString));
        dataList(queryString.string);
        showSuccess();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
      closeModal();
    },
  });

  const getCurrenciesList = async () => {
    const currencies = await Currency.currencies("allPages=true");
    const optionList = [];
    if (!currencies.error && currencies.data) {
      currencies.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        currenciesList: optionList,
      }));
    } else {
      showError(get(currencies, "error.messageCode", "ER000"));
    }
  };

  const getVerticalsList = async () => {
    const verticals = await Vertical.getVerticalList("allPages=true");
    const optionList = [];
    if (!verticals.error && verticals.data) {
      verticals.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        verticalsList: optionList,
        verticalListFilter: optionList,
      }));
    } else {
      showError(get(verticals, "error.messageCode", "ER000"));
    }
  };

  const openModal = () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    getStatusList();
    getVerticalsList();
    getCurrenciesList();
    setState((prevState) => ({ ...prevState, loading: false, modal: { visible: true, type: "new" } }));
  };

  const closeModal = () => {
    formikProjectEstimate.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isDisabled={!userInfo.canEditCostEstimation}
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={userInfo.canEditCostEstimation ? openModal : undefined}
        />
      </Row>
      <Filter formikForm={formikFilter} fields={fieldsFilter} loading={state.filterLoading} onReset={resetFilter} />
      <List
        header={<strong>{t("projectEstimate.table.estimates")}</strong>}
        bordered
        pagination={{
          size: "small",
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryParams = {
              page: page - 1,
              size: pageSize,
            };
            const queryString = buildQueryString(filters.obj, queryParams);
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
                currentPageProjectEstimate: state.currentPageProjectEstimate,
                totalCurrentProjectEstimate: state.totalCurrentProjectEstimate,
              },
            }));
            dataList(queryString.string);
          },
        }}
        dataSource={state.tableContent}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Row className="list-actions" key={item.id}>
                <Col onClick={() => navigate(`${item.id}`)}>
                  <EditOutlined className="icon-medium mr-10" />
                </Col>
                <Col onClick={deleteIfPossible(item)}>
                  <DeleteOutlined className="icon-medium" />
                </Col>
              </Row>,
            ]}
          >
            <List.Item.Meta
              title={
                <div>
                  {item.userSupportPm === null ? <WarningOutlined key={item.id} className="PFred mr-10" /> : ""}{" "}
                  {`${item.id} | ${item.description}`}
                </div>
              }
            />
          </List.Item>
        )}
      />
      <Modal
        width={700}
        title={<b>{t("projectEstimate.modal.newProjectEstimate")}</b>}
        open={state.modal.visible}
        onCancel={closeModal}
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikProjectEstimate.submitForm}
            isDisabled={!(formikProjectEstimate.dirty && formikProjectEstimate.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}

        <Row gutter={[40, 48]}>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="vertical">
                {"Vertical"}*
              </label>
            </Row>
            <Row>
              <Select
                className="w-100"
                id="vertical"
                name="vertical"
                placeholder={"vertical"}
                onChange={formikProjectEstimate.handleChange("verticalId")}
                onBlur={formikProjectEstimate.handleBlur("verticalId")}
                value={formikProjectEstimate.values.verticalId}
                options={state.verticalsList}
              />
            </Row>
          </Col>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="status">
                {t("projectEstimate.modal.status")}*
              </label>
            </Row>
            <Row>
              <Select
                className="w-100"
                id="status"
                name="status"
                placeholder={"status"}
                onChange={formikProjectEstimate.handleChange("projectEstimateStatusId")}
                onBlur={formikProjectEstimate.handleBlur("projectEstimateStatusId")}
                value={formikProjectEstimate.values.projectEstimateStatusId}
                options={state.statusListFilter}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="description">
                {t("projectEstimate.modal.description")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="description"
                name="description"
                onChange={formikProjectEstimate.handleChange}
                onBlur={formikProjectEstimate.handleBlur}
                value={formikProjectEstimate.values.description}
              />
            </Row>
          </Col>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="currency">
                {t("projectEstimate.modal.currency")}*
              </label>
            </Row>
            <Row>
              <Select
                className="w-100"
                id="currency"
                name="currency"
                placeholder={"currency"}
                onChange={formikProjectEstimate.handleChange("currencyId")}
                onBlur={formikProjectEstimate.handleBlur("currencyId")}
                value={formikProjectEstimate.values.currencyId}
                options={state.currenciesList}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default NotAssociatedEstimate;
